/**
 * The one place invite links are rendered for a human to hand out.
 *
 * There is no mailer in this system (see `backend/app/services/invites.py`).
 * A minted token is surfaced ONCE, on screen, and a person carries it the rest
 * of the way — so "a list of links with copy buttons and a CSV" is not a
 * convenience here, it is the delivery mechanism. It has two callers: the
 * campaign-launch success screen and Admin → Users' "chase the unredeemed
 * invites".
 *
 * Why this is a component and not a second copy
 * ---------------------------------------------
 * It used to be inline in `CampaignSetupView`, and the CSV export thirty lines
 * below it hand-rolled `${base}/invite/${token}` instead of calling the
 * formatter — a path with no server route. Every link a manager distributed
 * from that CSV 404'd. `lib/inviteLinks.ts` fixed the formatter; this fixes the
 * renderer, for the same reason: a second copy of a thing this load-bearing is
 * a second chance to get it wrong, and the wrongness is silent until somebody
 * out in the world clicks a dead link.
 *
 * Every URL on this screen comes from `inviteUrl`. Never build one by hand.
 */
import React, { useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';

import { inviteUrl } from '@/lib/inviteLinks';

/**
 * The minimum shape both callers can supply. The launch commit returns
 * `{email, full_name, role, token}`; the bulk re-issue returns those plus
 * `user_id` and `expires_at`, which this component ignores.
 */
export type InviteLink = {
  email: string;
  full_name?: string | null;
  role: string;
  token: string;
};

/** `sms_manager` -> `sms manager`. Roles are stored snake_case. */
function roleText(role: string): string {
  return role.replace(/_/g, ' ');
}

/**
 * Wrap one CSV field.
 *
 * Doubling embedded quotes is the part that is easy to skip: a name like
 * `Bob "Buzz" Aldrin` would otherwise close the field early and shift every
 * column after it — including the URL, which is the only column that matters.
 */
function csvField(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Build and download the roster CSV.
 *
 * Exported so a caller can trigger it from its own button placement without
 * re-deriving the column order or the URL format.
 */
export function downloadInvitesCsv(invites: InviteLink[], filename: string): void {
  const origin = window.location.origin;
  const csv = [
    'name,email,role,invite_url',
    ...invites.map((i) =>
      [
        csvField(i.full_name || ''),
        csvField(i.email),
        csvField(i.role),
        csvField(inviteUrl(origin, i.token)),
      ].join(','),
    ),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function InviteCsvButton({
  invites,
  filename,
  className,
}: {
  invites: InviteLink[];
  filename: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => downloadInvitesCsv(invites, filename)}
      disabled={invites.length === 0}
      className={
        className ??
        'inline-flex items-center gap-1.5 rounded border border-border-subtle px-4 py-2 text-sm font-semibold text-fg-secondary hover:bg-bg-elevated disabled:opacity-50'
      }
    >
      <Download className="h-3.5 w-3.5" />
      Download all as CSV
    </button>
  );
}

type Props = {
  invites: InviteLink[];
  className?: string;
  /** Rendered under each row — e.g. "this replaced a link that still worked". */
  noteFor?: (invite: InviteLink) => React.ReactNode;
};

export default function InviteLinkList({ invites, className, noteFor }: Props) {
  // Which row was copied last, keyed by email. Purely a 1.5s "Copied" flash;
  // the clipboard is the actual outcome.
  const [copied, setCopied] = useState<string | null>(null);

  if (invites.length === 0) return null;

  return (
    <div
      className={
        'divide-y divide-border-base rounded border border-border-base ' +
        (className ?? '')
      }
    >
      {invites.map((inv) => {
        const url = inviteUrl(window.location.origin, inv.token);
        const note = noteFor?.(inv);
        return (
          <div key={inv.email} className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-fg-primary">
                  {inv.full_name || inv.email}
                </div>
                <div className="truncate text-xs text-fg-muted">
                  {inv.email} &middot; {roleText(inv.role)}
                </div>
                {/* The URL itself, visible. A copy button whose result nobody
                    can see is a copy button nobody trusts — and this is the
                    string that has to be right. */}
                <code className="mt-1 block truncate text-[11px] text-fg-muted">
                  {url}
                </code>
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(url);
                  setCopied(inv.email);
                  window.setTimeout(() => setCopied(null), 1500);
                }}
                className="inline-flex shrink-0 items-center gap-1.5 rounded border border-border-subtle px-2.5 py-1.5 text-xs font-semibold text-fg-secondary hover:bg-bg-elevated"
              >
                {copied === inv.email ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-status-success" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy link
                  </>
                )}
              </button>
            </div>
            {note && <div className="mt-1 text-xs text-status-warning">{note}</div>}
          </div>
        );
      })}
    </div>
  );
}
